import { useRef, useMemo, useState, useEffect, ReactNode } from "react";

import dayjs from "dayjs";

import omitBy from "lodash/omitBy";
import debounce from "lodash/debounce";
import mergeWith from "lodash/mergeWith";
import { setDoc } from "firebase/firestore";
import isUndefined from "lodash/isUndefined";
import { useDocumentData } from "react-firebase-hooks/firestore";

import { Thankyou } from "@components/Thankyou/Thankyou";
import { PageLoader } from "@components/PageLoader/PageLoader";

import { getFormDocRef } from "../../services/form.services";

import { Draft } from "@utils/Draft.util";
import { IUser } from "@models/user.model";
import { getUserDocRef } from "../../services/user.services";
import { calculateProgress } from "@utils/calculateProgress.util";
import { IDemoRegistrationForm } from "@models/DemoRegistrationForm.model";
import { IEventRegistrationForm } from "@models/EventRegistrationForm.model";
import { getCalculateProgressParams } from "@utils/getCalculateProgressParams";
import { getVehiclesProgressParams } from "@utils/getVehiclesProgressParams.util";
import { getEventsProgressParamsUtil } from "@utils/getEventsProgressParams.util";
import { getDemoGroupProgressParams } from "@utils/getDemoGroupProgressParams.util";
import { IIVolunteeringFormData } from "@routes/VolunteeringFormRoute/VolunteeringFormRoute.types";
import { getContactAndBillingProgressParams } from "@utils/getContactAndBillingProgressParams.util";
import { getConditionsAcceptedProgressParamsUtil } from "@utils/getConditionsAcceptedProgressParams.util";

import FORM_DETAILS from "../../jsons/FORM_DETAILS.json";

const calculateVolunteeringFormProgress = (
  draft: Draft<IIVolunteeringFormData>
) => {
  const a = getCalculateProgressParams([
    draft.firstName,
    draft.lastName,
    draft.salutation,
    draft.phone?.code,
    draft.phone?.number,
    draft.email,
    draft.dob,
    draft.address,
    draft.postCode,
    draft.city,
  ]);
  const b = getConditionsAcceptedProgressParamsUtil(draft, true);
  return calculateProgress(a[0] + b[0], a[1] + b[1]);
};

const calculateDemoRegistrationFormProgress = (
  draft: Draft<IDemoRegistrationForm>
) => {
  const progresses: [number, number][] = [];
  progresses.push(getContactAndBillingProgressParams(draft));
  progresses.push(getDemoGroupProgressParams(draft.group || {}));
  progresses.push(getConditionsAcceptedProgressParamsUtil(draft, true));
  progresses.push(getVehiclesProgressParams(draft));

  return calculateProgress(
    ...progresses.reduce((pv, cv) => [pv[0] + cv[0], pv[1] + cv[1]], [0, 0])
  );
};

const calculateEventRegistrationFormProgress = (
  draft: Draft<IEventRegistrationForm>
) => {
  const progresses: [number, number][] = [];
  progresses.push(getContactAndBillingProgressParams(draft));
  progresses.push(getConditionsAcceptedProgressParamsUtil(draft));
  progresses.push(getEventsProgressParamsUtil(draft));

  return calculateProgress(
    ...progresses.reduce((pv, cv) => [pv[0] + cv[0], pv[1] + cv[1]], [0, 0])
  );
};

export const calculateFormProgress = (draft: Draft<any>, formName: string) => {
  let progress = 0;
  switch (formName) {
    case "volunteering": {
      progress = calculateVolunteeringFormProgress(
        draft as Draft<IIVolunteeringFormData>
      );
      break;
    }
    case "event-registration": {
      progress = calculateEventRegistrationFormProgress(
        draft as Draft<IEventRegistrationForm>
      );
      break;
    }
    case "demo-registration": {
      progress = calculateDemoRegistrationFormProgress(
        draft as Draft<IDemoRegistrationForm>
      );
      break;
    }
  }
  return progress;
};

type RenderProp<T extends Object> = (props: {
  user: IUser;
  isSaving: boolean;
  isSubmitted: boolean;
  isConfirmed: boolean;
  isSavePending: boolean;
  onSubmit: () => void;
  onSaveChanges: (changes: Draft<T>) => void;
  onForceSaveChanges: (changes: Draft<T>) => Promise<void>;
  draft?: Draft<T>;
}) => ReactNode;

export const FormProvider = <T extends Object>({
  uid,
  formName,
  children,
  ignoreSubmission = false,
  onSubmitSuccess = async () => {},
}: {
  uid: string;
  formName: string;
  children: RenderProp<T>;
  ignoreSubmission?: boolean;
  onSubmitSuccess?: () => Promise<void>;
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [showThankyou, setShowThankyou] = useState(false);
  const [isSavePending, setIsSavePending] = useState(false);

  const [user, isUserLoading] = useDocumentData(getUserDocRef(uid));

  const formDocRef = useRef(getFormDocRef<T>({ uid, formName }));

  const [formData, isFormDataLoading] = useDocumentData(formDocRef.current);

  const saveChanges = async (changes: Draft<T>) => {
    setIsSaving(true);
    const update = omitBy(
      mergeWith(form, changes, (draftValue, changesValue) => {
        if (Array.isArray(draftValue) && Array.isArray(changesValue))
          return changesValue;

        return;
      }),
      isUndefined
    ) as typeof changes;

    const progress = calculateFormProgress(update, formName);

    try {
      await setDoc(
        formDocRef.current,
        {
          user,
          progress,
          form: update,
          updatedAt: dayjs().valueOf(),
        },
        {
          merge: true,
        }
      );
    } catch (ex) {
      console.debug(ex);
    } finally {
      setIsSaving(false);
      setIsSavePending(false);
    }
  };

  const submitForm = async () => {
    setIsSaving(true);
    await setDoc(
      formDocRef.current,
      { user, isSubmitted: true, isConfirmed: true },
      { merge: true }
    );
    if (!formData?.isSubmitted) await onSubmitSuccess();
    setIsSaving(false);
    setShowThankyou(true);
  };

  const $toRefs = { saveChanges };
  const toRefs = useRef($toRefs);
  useEffect(() => {
    toRefs.current = $toRefs;
  });

  const debouncedSaveChanges = useMemo(
    () =>
      debounce<typeof saveChanges>(
        (...props) => toRefs.current.saveChanges(...props),
        1_000
      ),
    []
  );

  if (isFormDataLoading || isUserLoading || !user) return <PageLoader />;

  const form = formData?.form;

  const isSubmitted = !!formData?.isSubmitted;

  const key: { [key: string]: keyof typeof FORM_DETAILS } = {
    volunteering: "Volunteering",
    "demo-registration": "DemoRegistration",
    "event-registration": "EventRegistration",
  };

  if (
    (dayjs().isAfter(dayjs(FORM_DETAILS[key[formName]].dueDate)) &&
      !ignoreSubmission) ||
    showThankyou
  )
    return <Thankyou />;

  return (
    <>
      {children({
        user,
        isSaving,
        isSubmitted,
        isSavePending,
        draft: form,
        isConfirmed: !!formData?.isConfirmed,
        onSubmit: submitForm,
        onSaveChanges: (changes) => {
          setIsSavePending(true);
          debouncedSaveChanges(changes);
        },
        onForceSaveChanges: saveChanges,
      })}
    </>
  );
};
